// src/components/DeletionRequestsPage.js
// MODIFICADO: Se reemplaza message.error por un componente Alert para mostrar
// errores de generación de PDF de forma más visible.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Table, Tabs, Button, Popconfirm, message, Tag, Space, Tooltip, Modal, Input, Alert, Row, Col, DatePicker, Typography } from 'antd';
import { CheckOutlined, EyeOutlined, SyncOutlined, CloseCircleOutlined, FilePdfOutlined, UndoOutlined, DeleteFilled, SearchOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { deliveryService } from '../services/api';
import moment from 'moment';
import { useAuth } from '../context/AuthContext';
import debounce from 'lodash.debounce';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const DeletionRequestsPage = () => {
    const { user, fetchPendingCount } = useAuth(); 
    const navigate = useNavigate();
    const location = useLocation();
    
    const [activeTab, setActiveTab] = useState('pending');

    useEffect(() => {
        if (location.state?.activeTab) {
            const tabToActivate = location.state.activeTab;
            if (tabToActivate !== activeTab) {
                setActiveTab(tabToActivate);
            }
            navigate(location.pathname, { state: null, replace: true });
        }
    }, [location.state, activeTab, navigate]);
    
    const [loading, setLoading] = useState(false);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [deletionLogs, setDeletionLogs] = useState([]);
    const [eliminatedLists, setEliminatedLists] = useState([]);
    const [pdfLoadingId, setPdfLoadingId] = useState(null);
    const [pdfError, setPdfError] = useState(null); // Estado para el error del PDF

    const [logPagination, setLogPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [logSearchTerm, setLogSearchTerm] = useState('');
    const [logDateRange, setLogDateRange] = useState(null);

    const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
    const [recordToApprove, setRecordToApprove] = useState(null);
    const [approvalConfirmationText, setApprovalConfirmationText] = useState('');
    const [isApproving, setIsApproving] = useState(false);

    const [isPermanentDeleteModalVisible, setIsPermanentDeleteModalVisible] = useState(false);
    const [recordToPermanentlyDelete, setRecordToPermanentlyDelete] = useState(null);
    const [permanentDeleteConfirmationText, setPermanentDeleteConfirmationText] = useState('');
    const [isPermanentDeleting, setIsPermanentDeleting] = useState(false);

    const [isRestoreModalVisible, setIsRestoreModalVisible] = useState(false);
    const [recordToRestore, setRecordToRestore] = useState(null);
    const [isRestoring, setIsRestoring] = useState(false);
    const [restoreError, setRestoreError] = useState(null);

    const permissions = user?.role?.profile?.permissions?.actions?.deletionManagement;
    const canApprove = permissions?.canApproveRequest;
    const canReject = permissions?.canRejectRequest;
    const canViewLog = permissions?.canViewDeletionLog;
    const canRestore = permissions?.canRestoreRequest;
    const canPermanentlyDelete = permissions?.canPermanentlyDelete;
    const canViewRecycleBin = permissions?.canViewRecycleBin;

    const fetchPendingRequests = async () => {
        setLoading(true);
        try {
            console.log("esta consultando....");
            const response = await deliveryService.getDeletionRequests();
            if (response.data.success) { 
                setPendingRequests(response.data.data); 
                fetchPendingCount();//actualiza el badge
            }
        } catch (error) { console.error("Error fetching pending requests:", error); message.error("No se pudieron cargar las solicitudes pendientes."); }
        finally { setLoading(false); }
    };

    const fetchDeletionLogs = useCallback(async (page = 1, searchTerm = logSearchTerm, dateRange = logDateRange) => {
        if (!canViewLog) return;
        setLoading(true);
        try {
            const params = {
                page,
                limit: logPagination.pageSize,
                searchTerm: searchTerm,
            };
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.dateFrom = dateRange[0].startOf('day').toISOString();
                params.dateTo = dateRange[1].endOf('day').toISOString();
            }

            const response = await deliveryService.getDeletionLogs(params);
            if (response.data.success) {
                const { logs, total } = response.data.data;
                setDeletionLogs(logs);
                setLogPagination(prev => ({ ...prev, current: page, total }));
            }
        } catch (error) {
            console.error("Error fetching deletion logs:", error);
            message.error("No se pudo cargar el historial de eliminaciones.");
        } finally {
            setLoading(false);
        }
    }, [logPagination.pageSize, logSearchTerm, logDateRange, canViewLog]);
    
    const fetchEliminatedLists = async () => {
        setLoading(true);
        try {
            const response = await deliveryService.getEliminatedLists();
            if (response.data.success) { setEliminatedLists(response.data.data); }
        } catch (error) { console.error("Error fetching eliminated lists:", error); message.error("No se pudieron cargar los listados eliminados."); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (activeTab === 'pending') { fetchPendingRequests(); }
        else if (activeTab === 'eliminated' && canViewRecycleBin) { fetchEliminatedLists(); } 
        else if (activeTab === 'logs' && canViewLog) {
            fetchDeletionLogs(1, logSearchTerm, logDateRange);
        }
    }, [activeTab, canViewLog, canViewRecycleBin, logSearchTerm, logDateRange]);
    
    const showApprovalModal = (record) => { setRecordToApprove(record); setIsApprovalModalVisible(true); };
    const handleApprovalModalCancel = () => { setIsApprovalModalVisible(false); setRecordToApprove(null); setApprovalConfirmationText(''); };
    const handleApprovalModalOk = async () => {
        if (approvalConfirmationText !== recordToApprove?.packingListNumber) { message.error('El folio ingresado no coincide. Verifique la información.'); return; }
        setIsApproving(true);
        try {
            await deliveryService.approveDeletionRequest(recordToApprove.docEntry);
            message.success('Solicitud aprobada y listado eliminado.');
            fetchPendingRequests();
            handleApprovalModalCancel();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error al aprobar la solicitud.';
            message.error(errorMessage);
        } finally { setIsApproving(false); }
    };

    const handleReject = async (docEntry) => {
        setLoading(true);
        try {
            await deliveryService.rejectDeletionRequest(docEntry);
            message.success('Solicitud rechazada. El listado ha sido restaurado a activo.');
            fetchPendingRequests();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error al rechazar la solicitud.';
            message.error(errorMessage);
        } finally { setLoading(false); }
    };
    
    const handleViewPDF = async (record) => {
        setPdfLoadingId(record._id);
        setPdfError(null); // Limpiar errores anteriores
        try {
            await deliveryService.generatePDF(record._id);
        } catch (error) {
            const errorMessage = error.message || 'No se pudo generar el PDF del listado.';
            setPdfError("No se pudo generar el PDF del listado. Por favor, intente de nuevo más tarde."); //mostrar un mensaje más general
        } finally {
            setPdfLoadingId(null);
        }
    };
  
    const handleGoToEditor = (record) => {
        const path = record.formato === 'B' 
            ? `/packing-list-pfs/${record.docEntry}` 
            : `/packing-list/${record.docEntry}`;
        
        const navigationState = { 
            from: location.pathname, 
            readOnly: true,
            packingListId: record._id,
            activeTabKey: activeTab 
        };

        navigate(path, { state: navigationState });
    };

    const showRestoreModal = (record) => {
        setRecordToRestore(record);
        setRestoreError(null);
        setIsRestoreModalVisible(true);
    };

    const handleRestoreCancel = () => {
        setIsRestoreModalVisible(false);
        setRecordToRestore(null);
        setRestoreError(null);
    };

    const handleRestoreOk = async () => {
        if (!recordToRestore) return;
        setIsRestoring(true);
        setRestoreError(null);
        try {
            await deliveryService.restorePackingList(recordToRestore.docEntry);
            message.success(`Listado ${recordToRestore.packingListNumber} restaurado correctamente.`);
            fetchEliminatedLists();
            handleRestoreCancel();
        } catch (error) {
            console.error("ERROR AL RESTAURAR", error);
            if (error.response && error.response.status === 409) {
                setRestoreError(error.response.data.message || 'No se puede restaurar: ya existe un listado activo para esta entrega.');
            } else {
                setRestoreError('No es posible la restauración. Ocurrió un error inesperado.');
            }
        } finally {
            setIsRestoring(false);
        }
    };

    const showPermanentDeleteModal = (record) => { setRecordToPermanentlyDelete(record); setIsPermanentDeleteModalVisible(true); };
    const handlePermanentDeleteCancel = () => { setIsPermanentDeleteModalVisible(false); setRecordToPermanentlyDelete(null); setPermanentDeleteConfirmationText(''); };
    const handlePermanentDeleteOk = async () => {
        if (permanentDeleteConfirmationText !== recordToPermanentlyDelete?.packingListNumber) { message.error('El folio ingresado no coincide. Verifique la información.'); return; }
        setIsPermanentDeleting(true);
        try {
            await deliveryService.permanentlyDeletePackingList(recordToPermanentlyDelete.docEntry, recordToPermanentlyDelete._id);
            message.success(`Listado ${recordToPermanentlyDelete.packingListNumber} eliminado permanentemente.`);
            fetchEliminatedLists();
            handlePermanentDeleteCancel();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error en la eliminación permanente.';
            message.error(errorMessage);
        } finally { setIsPermanentDeleting(false); }
    };

    const pendingColumns = useMemo(() => [
        { title: 'Folio Listado', dataIndex: 'packingListNumber', key: 'packingListNumber', render: (text, record) => (<Tooltip title="Ver Editor (Solo Lectura)"><Tag color="warning" onClick={() => handleGoToEditor(record)} style={{ cursor: 'pointer' }}>{text}</Tag></Tooltip>) },
        { title: 'Doc. SAP', dataIndex: 'docNumSAP', key: 'docNumSAP' },
        { title: 'Fecha Solicitud', key: 'fechaSolicitud', render: (_, record) => moment(record.solicitudEliminacion?.fechaSolicitud).format('DD/MM/YYYY HH:mm') },
        { title: 'Solicitado Por', key: 'solicitadoPor', render: (_, record) => record.solicitudEliminacion?.solicitadoPor?.username || 'N/A' },
        { title: 'Motivo', dataIndex: ['solicitudEliminacion', 'motivo'], key: 'motivo' },
        {
            title: 'Acciones',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Ver Editor (Solo Lectura)"><Button icon={<EyeOutlined />} onClick={() => handleGoToEditor(record)}/></Tooltip>
                    <Tooltip title="Generar PDF del listado"><Button icon={<FilePdfOutlined />} onClick={() => handleViewPDF(record)} loading={pdfLoadingId === record._id}/></Tooltip>
                    {canReject && (<Popconfirm title="¿Está seguro de rechazar esta solicitud?" description="El listado volverá a estar activo." onConfirm={() => handleReject(record.docEntry)} okText="Sí, Rechazar" cancelText="No"><Tooltip title="Rechazar Solicitud"><Button icon={<CloseCircleOutlined />} /></Tooltip></Popconfirm>)}
                    {canApprove && (<Tooltip title="Aprobar Eliminación"><Button type="primary" danger icon={<CheckOutlined />} onClick={() => showApprovalModal(record)}/></Tooltip>)}
                </Space>
            ),
        },
    ], [canApprove, canReject, pdfLoadingId, activeTab, handleGoToEditor, handleViewPDF, handleReject, showApprovalModal]);

    const logsColumns = useMemo(() => [
        { title: 'Folio Listado', dataIndex: 'packingListNumber', key: 'packingListNumber', render: text => <Tag color="red">{text}</Tag> },
        { title: 'Doc. SAP', dataIndex: 'docNumSAP', key: 'docNumSAP' },
        { title: 'Fecha Eliminación', dataIndex: 'fechaEliminacion', key: 'fechaEliminacion', render: text => moment(text).format('DD/MM/YYYY HH:mm') },
        { title: 'Aprobado Por', key: 'aprobadoPor', render: (_, record) => record.aprobadoPor?.username || 'N/A' },
        { title: 'Motivo', dataIndex: 'motivo', key: 'motivo' },
        { title: 'Solicitado Por', key: 'solicitadoPor', render: (_, record) => record.solicitadoPor?.username || 'Directo' },
        { 
            title: 'Tipo de Eliminación', 
            dataIndex: 'deletionType', 
            key: 'deletionType',
            render: (type) => {
                const color = type === 'fisico' ? 'error' : 'warning';
                const text = type === 'fisico' ? 'FÍSICA' : 'LÓGICA';
                return <Tag color={color}>{text}</Tag>;
            }
        },
    ], []);
    
    const eliminatedColumns = useMemo(() => [
        { title: 'Folio Listado', dataIndex: 'packingListNumber', key: 'packingListNumber', render: (text, record) => (<Tooltip title="Ver Editor (Solo Lectura)"><Tag color="#a4a2a2" onClick={() => handleGoToEditor(record)} style={{ cursor: 'pointer' }}>{text}</Tag></Tooltip>) },
        { title: 'Doc. SAP', dataIndex: 'docNumSAP', key: 'docNumSAP' },
        { title: 'Fecha Eliminación', key: 'fechaEliminacion', render: (_, record) => moment(record.updatedAt).format('DD/MM/YYYY HH:mm') },
        { title: 'Solicitado Por', key: 'solicitadoPor', render: (_, record) => record.solicitudEliminacion?.solicitadoPor?.username || 'N/A (Directo)' },
        { 
            title: 'Motivo', 
            key: 'motivo',
            render: (_, record) => record.solicitudEliminacion?.motivo || 'No especificado'
        },
        {
            title: 'Acciones',
            key: 'action',
            render: (_, record) => (
                <Space>
                    {canRestore && (
                        <Tooltip title="Restaurar Listado">
                            <Button icon={<UndoOutlined />} type="primary" onClick={() => showRestoreModal(record)} />
                        </Tooltip>
                    )}
                    {canPermanentlyDelete && (<Tooltip title="Eliminar Permanentemente"><Button danger icon={<DeleteFilled />} onClick={() => showPermanentDeleteModal(record)} /></Tooltip>)}
                </Space>
            ),
        },
    ], [canRestore, canPermanentlyDelete, activeTab, handleGoToEditor, showRestoreModal, showPermanentDeleteModal]);

    const debouncedSearch = useCallback(debounce((value) => {
        setLogSearchTerm(value);
    }, 500), []);

    const handleLogTableChange = (pagination) => {
        fetchDeletionLogs(pagination.current, logSearchTerm, logDateRange);
    };
    
    const tabItems = [
        { key: 'pending', label: 'Solicitudes Pendientes', children: ( <Table columns={pendingColumns} dataSource={pendingRequests} rowKey="_id" loading={loading} pagination={{ pageSize: 10 }} /> ) },
        ...(canViewRecycleBin ? [{ key: 'eliminated', label: 'Papelera', children: ( <Table columns={eliminatedColumns} dataSource={eliminatedLists} rowKey="_id" loading={loading} pagination={{ pageSize: 10 }} /> ) }] : []),
        ...(canViewLog ? [{ key: 'logs', label: 'Historial de Eliminaciones', children: (
            <div>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <Text strong>Total de registros: {logPagination.total}</Text>
                    </Col>
                    <Col>
                        <Space>
                            <RangePicker onChange={(dates) => setLogDateRange(dates)} />
                            <Input
                                placeholder="Buscar por Folio, Doc. SAP o Motivo"
                                prefix={<SearchOutlined />}
                                allowClear
                                onChange={(e) => debouncedSearch(e.target.value)}
                                style={{ width: 300 }}
                            />
                        </Space>
                    </Col>
                </Row>
                <Table
                    columns={logsColumns}
                    dataSource={deletionLogs}
                    rowKey="_id"
                    loading={loading}
                    pagination={logPagination}
                    onChange={handleLogTableChange}
                />
            </div>
        ) }] : []),
    ];

    const handleRefresh = () => {
        if (activeTab === 'pending') { fetchPendingRequests(); }
        else if (activeTab === 'eliminated' && canViewRecycleBin) { fetchEliminatedLists(); }
        else if (activeTab === 'logs' && canViewLog) { fetchDeletionLogs(logPagination.current, logSearchTerm, logDateRange); }
    };

    return (
        <>
            {pdfError && (
                <Alert
                    message="Atención"
                    description={pdfError}
                    type="warning"
                    showIcon
                    closable
                    onClose={() => setPdfError(null)}
                    style={{ marginBottom: 16 }}
                />
            )}
            <Card title="Gestión de Eliminación de Listados" extra={<Button icon={<SyncOutlined />} onClick={handleRefresh} loading={loading}>Actualizar</Button>}>
                <Tabs 
                    activeKey={activeTab} 
                    items={tabItems} 
                    onChange={(key) => {
                        setActiveTab(key);
                        setPdfError(null); // Limpiar el error al cambiar de pestaña
                    }} 
                />
            </Card>

            <Modal title="Confirmar Aprobación de Eliminación" open={isApprovalModalVisible} onOk={handleApprovalModalOk} onCancel={handleApprovalModalCancel} confirmLoading={isApproving} okText="Confirmar Aprobación" cancelText="Cancelar" okButtonProps={{ disabled: approvalConfirmationText !== recordToApprove?.packingListNumber }}>
                {recordToApprove && ( <> <p>Está a punto de aprobar la eliminación del listado:</p> <p><strong>Folio:</strong> {recordToApprove.packingListNumber}</p> <p><strong>Doc. SAP:</strong> {recordToApprove.docNumSAP}</p> <p>El listado se eliminará.</p> <p>Para confirmar, por favor escriba el folio del listado <strong>({recordToApprove.packingListNumber})</strong> en el campo de abajo:</p> <Input placeholder="Escriba el folio para confirmar" value={approvalConfirmationText} onChange={(e) => setApprovalConfirmationText(e.target.value)} /> </> )}
            </Modal>

            <Modal
                title="Confirmar Restauración"
                open={isRestoreModalVisible}
                onOk={handleRestoreOk}
                onCancel={handleRestoreCancel}
                confirmLoading={isRestoring}
                okText="Sí, Restaurar"
                cancelText="Cancelar"
            >
                {recordToRestore && (
                    <>
                        <p>¿Está seguro de que desea restaurar el siguiente listado de empaque?</p>
                        <p><strong>Folio:</strong> {recordToRestore.packingListNumber}</p>
                        <p>El listado volverá a estar activo y disponible en el sistema.</p>
                        
                        {restoreError && (
                            <Alert
                                message="Error de Restauración"
                                description={restoreError}
                                type="error"
                                showIcon
                                style={{ marginTop: 16 }}
                            />
                        )}
                    </>
                )}
            </Modal>

            <Modal
                title="Confirmar Eliminación PERMANENTE"
                open={isPermanentDeleteModalVisible}
                onOk={handlePermanentDeleteOk}
                onCancel={handlePermanentDeleteCancel}
                confirmLoading={isPermanentDeleting}
                okText="Sí, Eliminar Permanentemente"
                cancelText="Cancelar"
                okButtonProps={{ danger: true, disabled: permanentDeleteConfirmationText !== recordToPermanentlyDelete?.packingListNumber }}
            >
                {recordToPermanentlyDelete && (
                    <>
                        <Alert message="¡Acción Irreversible!" description="Está a punto de eliminar este listado de forma permanente. No podrá ser recuperado." type="error" showIcon style={{marginBottom: 16}} />
                        <p><strong>Folio:</strong> {recordToPermanentlyDelete.packingListNumber}</p>
                        <p><strong>Doc. SAP:</strong> {recordToPermanentlyDelete.docNumSAP}</p>
                        <p>Para confirmar, por favor escriba el folio del listado <strong>({recordToPermanentlyDelete.packingListNumber})</strong> en el campo de abajo:</p>
                        <Input 
                            placeholder="Escriba el folio para confirmar"
                            value={permanentDeleteConfirmationText}
                            onChange={(e) => setPermanentDeleteConfirmationText(e.target.value)}
                        />
                    </>
                )}
            </Modal>
        </>
    );
};

export default DeletionRequestsPage;
